/**
 * Line-level partial payment (agreed rules):
 * - Eligible when this line’s full amount (price × quantity) is at least PARTIAL_MIN_LINE_DOLLARS.
 * - Minimum you can pay on a partial is PARTIAL_MIN_PAY_FRACTION of that line (25%).
 * - No env vars: adjust item line total in Woo only; quantity unchanged; fees on charged subtotal.
 */

/** Line must be at least this (price × qty) to show the optional partial amount. */
export const PARTIAL_MIN_LINE_DOLLARS = 1000;

/** Minimum payment as a fraction of the line list total (25%). */
export const PARTIAL_MIN_PAY_FRACTION = 0.25;

/**
 * Partial payments are disabled for now: the async order-creation queue creates a single
 * order per charge and does not model the pledge + initial-payment split partials require.
 * Every line is charged in full while this is off. Re-enable with
 * REACT_APP_PARTIAL_PAYMENTS_ENABLED=true (and restore the backend pledge-split flow).
 */
export const PARTIAL_PAYMENTS_ENABLED =
  String(process.env.REACT_APP_PARTIAL_PAYMENTS_ENABLED || '').toLowerCase() === 'true';

export function getLineListTotal(item) {
  if (!item || typeof item !== 'object') return 0;
  return (Number(item.price) || 0) * (Number(item.quantity) || 1);
}

/**
 * Eligible partial line was cleared (X) and user has not re-entered an amount yet.
 * Charge for this line is treated as 0 for totals; checkout should block until filled.
 */
export function isPartialLineAmountUnset(item) {
  if (!isPartialPaymentEligible(item)) return false;
  if (!item.pay_line_cleared) return false;
  const raw = item.pay_line_total;
  if (raw == null || raw === '') return true;
  const n = parseFloat(String(raw).replace(/,/g, ''));
  return !Number.isFinite(n);
}

/** Dollar amount to charge for this line (Woo line subtotal/total). */
export function getLineChargeTotal(item) {
  const list = getLineListTotal(item);
  if (list <= 0) return 0;
  if (!isPartialPaymentEligible(item)) {
    return list;
  }
  if (item.pay_line_cleared) {
    const raw = item.pay_line_total;
    if (raw == null || raw === '') return 0;
  } else {
    const raw0 = item.pay_line_total;
    if (raw0 == null || raw0 === '') return list;
  }
  const n = parseFloat(String(item.pay_line_total).replace(/,/g, ''));
  if (!Number.isFinite(n)) {
    if (item.pay_line_cleared) return 0;
    return list;
  }
  const min = list * PARTIAL_MIN_PAY_FRACTION;
  return Math.min(list, Math.max(min, n));
}

export function isPartialPaymentEligible(item) {
  if (!PARTIAL_PAYMENTS_ENABLED) return false;
  return getLineListTotal(item) >= PARTIAL_MIN_LINE_DOLLARS;
}

export function getPartialMinPay(item) {
  if (!isPartialPaymentEligible(item)) return null;
  return getLineListTotal(item) * PARTIAL_MIN_PAY_FRACTION;
}

/** When true, WooCommerce line subtotal/total should be set to the charge amount. */
export function isLineItemCustomTotal(item) {
  if (isPartialLineAmountUnset(item)) return false;
  const list = getLineListTotal(item);
  const ch = getLineChargeTotal(item);
  return list > 0 && ch + 0.005 < list;
}
